import { apiClient } from '@/api/client'
import type { ApiResponse, ItemEntity, Warehouse } from '@/api/types'
import { endpoints } from '@/api/endpoints'
import type { UnsellableMovementRow } from '@/modules/inventory/unsellable-movement/types'
import { inventoryMovementService } from '@/services/inventoryMovementService'

function formatWarehouseLabel(w: Warehouse): string {
  const c = w.code?.trim()
  const n = w.name?.trim()
  if (c && n) return `${c} — ${n}`
  return n || c || String(w.id)
}

function formatItemLabel(it: ItemEntity): string {
  const name = it.itemNameEn?.trim() || it.itemNameAr?.trim() || ''
  const code = it.itemCode?.trim() || ''
  if (code && name) return `${code} — ${name}`
  return name || code || String(it.itemId)
}

export interface InventoryUnsellableSavePayload {
  id: number
  code: string
  warehouseId: number
  itemId: number
  batchId: number
  date: string
  prevAvailableQuantity: number
  movedToDamaged: number
  movedToExpired: number
  newAvailableQuantity: number
  branchId: number
}

function unwrapData<T>(payload: unknown): T {
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    if ('success' in p && 'data' in p) return p.data as T
    if ('data' in p) return p.data as T
  }
  return payload as T
}

function parseReturnedId(payload: unknown): number | undefined {
  const data = unwrapData<Record<string, unknown>>(payload)
  if (!data || typeof data !== 'object') return undefined
  const raw = data.id ?? data.Id
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

function responseRootIsErrorFlag(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false
  const r = payload as Record<string, unknown>
  if (r.isError === true || r.IsError === true) return true
  const inner = r.data ?? r.Data
  if (inner && typeof inner === 'object') {
    const o = inner as Record<string, unknown>
    if (o.isError === true || o.IsError === true) return true
  }
  return false
}

function rowMatchesSavePayload(row: UnsellableMovementRow, body: InventoryUnsellableSavePayload): boolean {
  if (Number(row.warehouseId) !== body.warehouseId) return false
  if (Number(row.itemId) !== body.itemId) return false
  if (Number(row.batchId) !== body.batchId) return false
  const d = (row.movementDate || '').slice(0, 10)
  if (d !== body.date.slice(0, 10)) return false
  if (row.previousQuantity !== body.prevAvailableQuantity) return false
  if (row.damaged !== body.movedToDamaged) return false
  if (row.expired !== body.movedToExpired) return false
  if (row.newQuantity !== body.newAvailableQuantity) return false
  if (body.id > 0 && row.apiId !== body.id) return false
  return true
}

/** Map API list row → UI row (camelCase / PascalCase tolerant). */
export function mapApiToUnsellableMovementRow(x: Record<string, unknown>): UnsellableMovementRow {
  const idNum = Number(x.id ?? x.Id ?? 0)
  const itemCode = String(x.itemCode ?? x.ItemCode ?? '').trim()
  const itemName = String(
    x.itemName ?? x.ItemName ?? x.itemNameEn ?? x.ItemNameEn ?? x.itemNameAr ?? x.ItemNameAr ?? '',
  ).trim()
  const itemLabel =
    itemCode && itemName ? `${itemCode} — ${itemName}` : itemName || itemCode || String(x.itemId ?? x.ItemId ?? '')

  const batchLabel = String(
    x.batchCode ?? x.BatchCode ?? x.batch ?? x.Batch ?? x.batchName ?? x.BatchName ?? x.batchLabel ?? x.BatchLabel ?? '',
  ).trim()

  const dateRaw = String(x.date ?? x.Date ?? x.movementDate ?? x.MovementDate ?? '').trim()

  return {
    id: idNum > 0 ? `api-${idNum}` : `tmp-${Math.random().toString(36).slice(2, 11)}`,
    apiId: idNum > 0 ? idNum : undefined,
    code: String(x.code ?? x.Code ?? ''),
    warehouseId: String(x.warehouseId ?? x.WarehouseId ?? ''),
    warehouseName: String(
      x.warehouseName ?? x.WarehouseName ?? x.warehouse ?? x.Warehouse ?? x.warehouseCode ?? x.WarehouseCode ?? '',
    ).trim(),
    itemId: String(x.itemId ?? x.ItemId ?? ''),
    itemLabel,
    batchId: String(x.batchId ?? x.BatchId ?? ''),
    batchLabel: batchLabel || String(x.batchId ?? x.BatchId ?? ''),
    previousQuantity: Number(
      x.prevAvailableQuantity ?? x.PrevAvailableQuantity ?? x.previousQuantity ?? x.PreviousQuantity ?? 0,
    ),
    damaged: Number(x.movedToDamaged ?? x.MovedToDamaged ?? x.damaged ?? x.Damaged ?? 0),
    expired: Number(x.movedToExpired ?? x.MovedToExpired ?? x.expired ?? x.Expired ?? 0),
    newQuantity: Number(x.newAvailableQuantity ?? x.NewAvailableQuantity ?? x.newQuantity ?? x.NewQuantity ?? 0),
    movementDate: dateRaw.slice(0, 10),
  }
}

const LOG = '[InventoryMovement/Save]'

export class UnsellableInventoryMovementService {
  async getAll(
    branchId: number,
    pageNumber: number,
    pageSize: number,
  ): Promise<{ items: UnsellableMovementRow[]; totalCount: number }> {
    const url = endpoints.inventoryUnsellableMovement.getAll(branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(url)
    const rawRoot = response.data as Record<string, unknown> | null | undefined
    const payload = unwrapData<unknown>(response.data)

    let arr: unknown[] = []
    if (Array.isArray(payload)) {
      arr = payload
    } else if (payload && typeof payload === 'object') {
      const p = payload as Record<string, unknown>
      if (Array.isArray(p.data)) arr = p.data as unknown[]
      else if (Array.isArray(p.items)) arr = p.items as unknown[]
      else if (Array.isArray(p.Data)) arr = p.Data as unknown[]
      else if (Array.isArray(p.Items)) arr = p.Items as unknown[]
    }
    if (arr.length === 0 && rawRoot && Array.isArray(rawRoot.data)) {
      arr = rawRoot.data as unknown[]
    }

    const totalCount = Number(
      rawRoot?.totalRecords ??
        rawRoot?.totalCount ??
        rawRoot?.TotalRecords ??
        rawRoot?.TotalCount ??
        (payload && typeof payload === 'object'
          ? (payload as Record<string, unknown>).totalRecords ??
            (payload as Record<string, unknown>).totalCount ??
            (payload as Record<string, unknown>).TotalRecords ??
            (payload as Record<string, unknown>).TotalCount
          : undefined) ??
        arr.length,
    )

    const items = arr
      .filter((row): row is Record<string, unknown> => row !== null && typeof row === 'object')
      .map((row) => mapApiToUnsellableMovementRow(row))

    const enriched = await this.enrichListRowsWithLookups(branchId, items)
    return { items: enriched, totalCount: Number.isFinite(totalCount) ? totalCount : items.length }
  }

  /** Single movement by server id (Swagger: GetInventoryMovementById). */
  async getById(id: number): Promise<UnsellableMovementRow> {
    const url = endpoints.inventoryUnsellableMovement.getById(id)
    const response = await apiClient.get<ApiResponse<unknown> | unknown>(url)
    const raw = response.data
    const obj = unwrapData<Record<string, unknown>>(raw)
    if (!obj || typeof obj !== 'object') {
      throw new Error('Invalid GetInventoryMovementById response')
    }
    const branchId = Number(obj.branchId ?? obj.BranchId ?? 0)
    let row = mapApiToUnsellableMovementRow(obj)
    if (branchId > 0) {
      const [enriched] = await this.enrichListRowsWithLookups(branchId, [row])
      if (enriched) row = enriched
    }
    return row
  }

  /**
   * List API returns warehouseId / itemId / batchId only — resolve labels via InventoryTransfer APIs.
   */
  private async enrichListRowsWithLookups(
    branchId: number,
    rows: UnsellableMovementRow[],
  ): Promise<UnsellableMovementRow[]> {
    if (rows.length === 0) return rows

    const whLabelById = new Map<number, string>()
    try {
      const warehouses = await inventoryMovementService.getWarehousesByBranchId(branchId)
      for (const w of warehouses) {
        whLabelById.set(w.id, formatWarehouseLabel(w))
      }
    } catch {
      // keep map empty; fall back to ids below
    }

    const uniqueWhIds = [
      ...new Set(
        rows.map((r) => Number(r.warehouseId)).filter((n) => Number.isFinite(n) && n > 0),
      ),
    ]

    const itemLabelByWhItem = new Map<string, string>()
    await Promise.all(
      uniqueWhIds.map(async (whId) => {
        try {
          const list = await inventoryMovementService.getItemsByWarehouse(whId)
          for (const it of list) {
            itemLabelByWhItem.set(`${whId}-${it.itemId}`, formatItemLabel(it))
          }
        } catch {
          /* skip warehouse */
        }
      }),
    )

    const whItemPairs = [
      ...new Set(
        rows
          .map((r) => {
            const w = Number(r.warehouseId)
            const i = Number(r.itemId)
            if (!Number.isFinite(w) || w <= 0 || !Number.isFinite(i) || i <= 0) return ''
            return `${w}-${i}`
          })
          .filter(Boolean),
      ),
    ]

    const batchCodeByTriple = new Map<string, string>()
    await Promise.all(
      whItemPairs.map(async (key) => {
        const [a, b] = key.split('-')
        const whId = Number(a)
        const itemId = Number(b)
        try {
          const batches = await inventoryMovementService.getBatchesByWarehouseAndItem(whId, itemId)
          for (const batch of batches) {
            const code = batch.code?.trim() || String(batch.id)
            batchCodeByTriple.set(`${whId}-${itemId}-${batch.id}`, code)
          }
        } catch {
          /* skip pair */
        }
      }),
    )

    return rows.map((r) => {
      const whId = Number(r.warehouseId)
      const itemId = Number(r.itemId)
      const batchId = Number(r.batchId)

      const warehouseName =
        (Number.isFinite(whId) && whId > 0 ? whLabelById.get(whId) : undefined) ??
        r.warehouseName?.trim() ??
        (Number.isFinite(whId) && whId > 0 ? String(whId) : '')

      const itemResolved =
        Number.isFinite(whId) && whId > 0 && Number.isFinite(itemId) && itemId > 0
          ? itemLabelByWhItem.get(`${whId}-${itemId}`)
          : undefined
      const itemLabel =
        itemResolved ?? r.itemLabel?.trim() ?? (Number.isFinite(itemId) && itemId > 0 ? String(itemId) : '')

      const batchResolved =
        Number.isFinite(whId) &&
        whId > 0 &&
        Number.isFinite(itemId) &&
        itemId > 0 &&
        Number.isFinite(batchId) &&
        batchId > 0
          ? batchCodeByTriple.get(`${whId}-${itemId}-${batchId}`)
          : undefined
      const batchLabel =
        batchResolved ?? r.batchLabel?.trim() ?? (Number.isFinite(batchId) && batchId > 0 ? String(batchId) : '')

      return { ...r, warehouseName, itemLabel, batchLabel }
    })
  }

  /**
   * Some backends persist the row then throw (500 / isError) when building the response.
   * Confirm by re-reading the first page of movements for this branch.
   */
  private async tryRecoverIdAfterAmbiguousSave(body: InventoryUnsellableSavePayload): Promise<number | undefined> {
    await new Promise((r) => setTimeout(r, 400))
    try {
      const { items } = await this.getAll(body.branchId, 1, 200)
      const matches = items.filter((row) => rowMatchesSavePayload(row, body))
      if (matches.length === 0) return undefined
      matches.sort((a, b) => (b.apiId ?? 0) - (a.apiId ?? 0))
      const best = matches[0]?.apiId
      return best != null && best > 0 ? best : undefined
    } catch {
      return undefined
    }
  }

  async save(body: InventoryUnsellableSavePayload): Promise<number | undefined> {
    const url = endpoints.inventoryUnsellableMovement.save
    console.log(`${LOG} URL:`, url)
    console.log(`${LOG} Request body (exact JSON):`, JSON.stringify(body, null, 2))

    try {
      const response = await apiClient.post<ApiResponse<unknown> | unknown>(url, body, {
        skipGlobal500Swal: true,
      } as any)
      console.log(`${LOG} Response status:`, response.status)
      console.log(`${LOG} Response data (raw JSON):`, JSON.stringify(response.data, null, 2))

      if (responseRootIsErrorFlag(response.data)) {
        console.warn(`${LOG} Response flagged as error; verifying list for persisted row…`)
        const recovered = await this.tryRecoverIdAfterAmbiguousSave(body)
        if (recovered != null) return recovered
        const root = response.data as Record<string, unknown>
        const text = String(root.msg ?? root.Msg ?? root.message ?? root.Message ?? 'Save failed')
        throw new Error(text)
      }

      return parseReturnedId(response.data)
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: unknown }; message?: string }
      console.error(`${LOG} Request failed`)
      console.error(`${LOG} Response status:`, err?.response?.status)
      console.error(`${LOG} Response data (raw JSON):`, JSON.stringify(err?.response?.data ?? null, null, 2))
      console.error(`${LOG} Error message:`, err?.message)

      if (err?.response?.status === 500) {
        const recovered = await this.tryRecoverIdAfterAmbiguousSave(body)
        if (recovered != null) {
          console.warn(`${LOG} Treating as success: row found after HTTP 500 (id=${recovered}).`)
          return recovered
        }
      }

      const pascalHint = {
        Id: body.id,
        Code: body.code,
        WarehouseId: body.warehouseId,
        ItemId: body.itemId,
        BatchId: body.batchId,
        Date: body.date,
        PrevAvailableQuantity: body.prevAvailableQuantity,
        MovedToDamaged: body.movedToDamaged,
        MovedToExpired: body.movedToExpired,
        NewAvailableQuantity: body.newAvailableQuantity,
        BranchId: body.branchId,
      }
      console.warn(
        `${LOG} If the server expects PascalCase JSON, try this shape (compare with Swagger/backend):`,
        JSON.stringify(pascalHint, null, 2),
      )
      throw e
    }
  }
}

export const unsellableInventoryMovementService = new UnsellableInventoryMovementService()
